import { DropzoneField } from '@lfg/ui/components';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, Grid2 as Grid, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { NFTAssetUpload } from 'src/components/NFTAssetUpload';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';

import { AdminInput } from '../AdminTextField';
import { StepAccordion } from '../StepAccordion';

interface TokenConfigurationProps {
  programId?: string;
  isDisabled?: boolean;
  assets?: Array<{
    id: string;
    name: string;
    size: number;
  }>;
}

export function TokenConfiguration({ programId, isDisabled, assets }: TokenConfigurationProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectInputs>();
  const { hasDeployed } = useDeployment();

  const handleDownloadTemplate = useCallback(() => {
    const templateUrl = '/resources/files/templates/nfts-template.zip';
    const link = document.createElement('a');
    link.href = templateUrl;
    link.download = 'nfts-template.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return (
    <StepAccordion isError={!!errors.node} title={'NFT Configuration'} index={2}>
      <Typography variant='body1' fontSize={'14px'} fontWeight={400} sx={{ color: 'white' }} mb={3}>
        Each NFT is minted as an ERC-721 Token on Ethereum, representing unique ownership of your NFT.
      </Typography>

      <Grid container columnSpacing={2}>
        <Grid size={12}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              NFT Name
            </Typography>
            <Controller
              name='node.name'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={hasDeployed || isDisabled}
                  isError={!!errors?.node?.name}
                  errorMsg={errors.node?.name?.message}
                  placeholder={'Enter name'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={12} marginTop={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              NFT Symbol
            </Typography>
            <Controller
              name='node.symbol'
              control={control}
              render={({ field }) => (
                <AdminInput
                  readOnly={hasDeployed || isDisabled}
                  isError={!!errors?.node?.symbol}
                  errorMsg={errors.node?.symbol?.message}
                  placeholder={'Enter symbol name'}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={12} marginTop={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              NFT Description
            </Typography>
            <Controller
              name='node.description'
              control={control}
              render={({ field }) => (
                <AdminInput
                  rows={5}
                  readOnly={hasDeployed || isDisabled}
                  inputHeight='140px'
                  multiline
                  isError={!!errors?.node?.description}
                  errorMsg={errors.node?.description?.message}
                  placeholder={'Give a short description about the project’s NFT...'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      padding: 0,
                    },
                  }}
                  {...field}
                />
              )}
            />
          </Box>
        </Grid>
        {/* NFT Asset (Image) */}
        <Grid size={12} marginTop={3}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              NFT Asset
            </Typography>
            <Controller
              name={'node.asset'}
              control={control}
              rules={{ required: 'Please upload at least one file' }}
              render={({ field }) => (
                <DropzoneField
                  disabled={hasDeployed || isDisabled}
                  maxFiles={1}
                  containerSx={{
                    border: `1px dashed ${errors.node?.asset ? colors.error : colors.neutral015}`,
                    backgroundColor: colors.neutral03,
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                    maxHeight: '200px',
                  }}
                  onDrop={(files: File[]) => field.onChange(files)}
                  files={Array.isArray(field.value) ? (field.value.filter(f => f instanceof File) as File[]) : []}
                  onClear={() => field.onChange([])}
                  accept={{
                    'image/jpeg': [],
                    'image/png': [],
                    'image/gif': [],
                  }}
                />
              )}
            />
            {errors.node?.asset ? (
              <Typography color={colors.error} fontSize={'14px'} paddingTop={1} variant='h6'>
                {errors.node?.asset?.message}
              </Typography>
            ) : null}
            <ul className='ml-[-28px] flex gap-x-8 items-center text-sm text-[#9C9BA3]'>
              <li>Supported formats: jpeg, png, gif</li>
              <li>Maximum file size: 10MB </li>
            </ul>
          </Box>
        </Grid>

        {/* NFT Assets ZIP (kept feature) */}
        <Grid size={12} marginTop={3}>
          <Stack width={'100%'} gap={3}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' width={'100%'}>
              <Typography color={colors.neutral200} fontSize={'14px'} fontWeight={500} variant='h6'>
                NFT Assets ZIP
              </Typography>
              <Button
                variant='outlined'
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                disabled={hasDeployed || isDisabled}
                sx={{
                  borderColor: colors.neutral015,
                  color: colors.white,
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: colors.neutral60,
                    backgroundColor: colors.neutral015,
                  },
                }}>
                Download Template
              </Button>
            </Stack>

            <Box width={'100%'}>
              <Controller
                name={'node.assets' as any}
                control={control}
                render={({ field }) =>
                  programId ? (
                    <NFTAssetUpload
                      programId={programId}
                      disabled={hasDeployed || isDisabled}
                      error={undefined}
                      initialAsset={assets && assets.length > 0 ? assets[0] : null}
                      onUploadComplete={assetId => {
                        field.onChange([assetId]);
                      }}
                      onClear={() => {
                        field.onChange([]);
                      }}
                    />
                  ) : (
                    <Typography color={colors.neutral90} fontSize={'14px'}>
                      Loading...
                    </Typography>
                  )
                }
              />
              <ul className='ml-[-28px] flex gap-x-8 items-center text-sm text-[#9C9BA3] mt-2'>
                <li>Supported format: ZIP file</li>
              </ul>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </StepAccordion>
  );
}
